import React, { useState, useEffect, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Modal, Checkbox, Select } from "antd";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
import store from "store";
import Storage from "utils/storage";
import XtSvg from "@/components/Xt/Svg";
import styles from "./index.module.scss";

const { useTranslation } = Hooks;

interface Props {
  open: boolean;
  onClose: () => void;
}

type KlineColorScheme = "green_up" | "red_up"; // 绿涨红跌 | 红涨绿跌

const TradingSettingsModal: React.FC<Props> = ({ open, onClose }) => {
  const t = useTranslation();
  // 临时状态：下单二次确认（只在弹窗内使用，不立即保存）
  const [orderConfirm, setOrderConfirm] = useState(true);
  // 临时状态：平仓二次确认（只在弹窗内使用，不立即保存）
  const [closePositionConfirm, setClosePositionConfirm] = useState(true);
  
  // 临时状态：K线颜色方案（只在弹窗内使用，不立即保存）
  const [klineColorScheme, setKlineColorScheme] = useState<KlineColorScheme>("green_up");

  // 初始化设置：从Storage读取当前值
  useEffect(() => {
    if (open) {
      // 读取下单二次确认设置（默认开启，如果 Storage 中没有值则默认为 true）
      const orderConfirm_limit = Storage.get("orderConfirm_limit");
      const orderConfirm_market = Storage.get("orderConfirm_market");
      const closeConfirm_position = Storage.get("closeConfirm_position");
      // 如果两个都没有设置，默认开启；如果任一为true则显示为true
      const defaultConfirm = orderConfirm_limit === undefined && orderConfirm_market === undefined ? true : (orderConfirm_limit !== false || orderConfirm_market !== false);
      setOrderConfirm(defaultConfirm);
      setClosePositionConfirm(closeConfirm_position === undefined || closeConfirm_position === null ? true : closeConfirm_position !== false);

      // 读取K线颜色方案设置（默认绿涨红跌）
      const savedScheme = Storage.get("klineColorScheme") as KlineColorScheme;
      if (savedScheme === "red_up" || savedScheme === "green_up") {
        setKlineColorScheme(savedScheme);
      } else {
        // 如果没有设置，默认绿涨红跌
        setKlineColorScheme("green_up");
      }
    }
  }, [open]);

  // 处理下单二次确认开关变化（只更新临时状态，不保存）
  const handleOrderConfirmChange = useCallback((checked: boolean) => {
    setOrderConfirm(checked);
  }, []);

  // 处理平仓二次确认开关变化（只更新临时状态，不保存）
  const handleClosePositionConfirmChange = useCallback((checked: boolean) => {
    setClosePositionConfirm(checked);
  }, []);

  // 处理K线颜色方案变化（只更新临时状态，不保存）
  const handleKlineColorSchemeChange = useCallback((value: KlineColorScheme) => {
    setKlineColorScheme(value);
  }, []);

  // K线颜色方案选项
  const klineColorOptions = useMemo(
    () => [
      { label: t("cfd.greenUpRedDown"), value: "green_up" },
      { label: t("cfd.redUpGreenDown"), value: "red_up" },
    ],
    []
  );

  // 处理确定按钮：保存设置并关闭弹窗
  const handleConfirm = useCallback(() => {
    // 保存下单二次确认设置
    store.trade.updateState({
      orderConfirm_limit: orderConfirm,
      orderConfirm_market: orderConfirm,
      closeConfirm_position: closePositionConfirm,
    });
    Storage.set("orderConfirm_limit", orderConfirm);
    Storage.set("orderConfirm_market", orderConfirm);
    Storage.set("closeConfirm_position", closePositionConfirm);

    // 保存K线颜色方案设置
    Storage.set("klineColorScheme", klineColorScheme);
    // 触发自定义事件，通知其他组件颜色方案已变化
    window.dispatchEvent(new Event("klineColorSchemeChange"));

    // 关闭弹窗
    onClose();
  }, [orderConfirm, closePositionConfirm, klineColorScheme, onClose]);

  return (
    <Modal
      className={styles.modal}
      open={open}
      title={null}
      footer={null}
      onCancel={onClose}
      closeIcon={<XtSvg icon="close" />}
      width={343}
      centered
    >
      <div className={styles.content}>
        {/* 标题和关闭按钮 */}
        <div className={styles.header}>
          <div className={styles.title}>{t("cfd.tradingSettings")}</div>
        </div>
        {/* 下单二次确认 */}
        <div className={styles.settingItem}>
          <div className={styles.settingLabel}>{t("cfd.orderConfirm")}</div>
          <Checkbox
            checked={orderConfirm}
            onChange={(e) => handleOrderConfirmChange(e.target.checked)}
            className={styles.checkbox}
          />
        </div>

        <div className={styles.settingItem}>
          <div className={styles.settingLabel}>{t("cfd.closeConfirm")}</div>
          <Checkbox
            checked={closePositionConfirm}
            onChange={(e) => handleClosePositionConfirmChange(e.target.checked)}
            className={styles.checkbox}
          />
        </div>

        {/* K线设置 */}
        <div className={styles.klineSetting}>
          <div className={styles.settingLabel}>{t("cfd.klineSettings")}</div>
          <Select
            value={klineColorScheme}
            onChange={handleKlineColorSchemeChange}
            options={klineColorOptions}
            className={styles.select}
            dropdownClassName={styles.selectDropdown}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
          />
        </div>

        {/* 确定按钮 */}
        <div className={styles.footer}>
          <button className={styles.confirmBtn} onClick={handleConfirm}>
            {t("cfd.confirm")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default observer(TradingSettingsModal);

